import React, { useEffect, useContext, useState, useRef } from 'react'
import { Box, Button } from '@mui/material'

import EditableField from "./EditableField"

import simpleArrowIcon from "../../../images/simpleArrowIcon.svg"

// L5 / L23 : fleche ">" a droite du libelle + 40px de padding horizontal.
// La fleche est le SVG maison (noir), passee en blanc via filter pour les
// boutons pleins.
/* ⚠️ CE N'EST PAS UNE FLECHE (relu dans Figma le 22/09, nodes 3:20303 et
   3:40483). C'est un CHEVRON PIXELLISE : cinq carres blancs de 2,398 x 2,4 px
   poses en escalier a (0;0) (2,39;2,4) (4,81;4,8) (2,39;7,2) (0;9,6), pour un
   encombrement de 7,21 x 12. Le depot posait `simpleArrowIcon.svg`, une fleche
   PLEINE AVEC HAMPE en 16 x 13 blanchie par un filtre : ni la meme forme, ni la
   meme taille, sur les 82 boutons du produit.
   Le trace est TRANSCRIT depuis les coordonnees du node, pas dessine a vue :
   chaque carre est a sa position exacte. */
const fleche = (
  <svg className="hs-cta-fleche" width="7.21" height="12" viewBox="0 0 7.21 12" fill="none" aria-hidden="true"
       style={{display:"block", flexShrink:0}}>
    <rect x="0"    y="0"   width="2.398" height="2.4" fill="#FFFFFF" />
    <rect x="2.39" y="2.4" width="2.398" height="2.4" fill="#FFFFFF" />
    <rect x="4.81" y="4.8" width="2.398" height="2.4" fill="#FFFFFF" />
    <rect x="2.39" y="7.2" width="2.398" height="2.4" fill="#FFFFFF" />
    <rect x="0"    y="9.6" width="2.398" height="2.4" fill="#FFFFFF" />
  </svg>
)

/* ⛔ 04/10/2026 — LE GRAND BOUTON DE LA PLANCHE TITRE (3:161299 Desktop,
   3:161345 Mobile) : 189,8 x 70 (167,8 x 60 en Mobile), libelle 20 px (18 en
   Mobile) a 40 px du bord gauche (30), 15 px avant le chevron, chevron de
   12 x 20 (10 x 16,7), 30 px a droite (20). Le seul ecran qui le dessine. */
const GRAND = {
  fontSize:["18px !important", "20px !important"],
  lineHeight:["27px !important", "30px !important"],
  height:["60px !important", "70px !important"],
  minHeight:["60px !important", "70px !important"],
  minWidth:["167.8px", "189.8px"],
  paddingLeft:["30px", "40px"],
  paddingRight:["20px", "30px"],
  justifyContent:"flex-start",
  "& .hs-cta-libelle": { flex:"0 0 auto", minWidth:"92.8px", padding:0, textAlign:"center" },
  "& .hs-cta-fleche": { width:["10px", "12px"], height:["16.7px", "20px"], marginLeft:"15px" },
}

/* `reste` (07/10/2026) : attributs passes tels quels au bouton hors editeur
   (ex. `data-voir-certificat`, le meme CTA reutilise dans « Mon résultat »). */
const EditableButton = ({type, buttonName, field, isEditor, sx, onClick, disabled, grand, ...reste}) => {
  if (isEditor) {
    return (
      <Button
        sx={{
          ...sx,
          /* ⚠️ RELU DANS FIGMA (21/09/2026, lot 3d), noeuds 3:20302 et
             3:40482 : 16 px / interligne 26, IDENTIQUE aux trois paliers. Le
             code descendait a 15 px en Mobile. */
          fontSize:"16px !important",
          lineHeight:"26px !important",
          /* Aucun interlettrage : MUI pose 0,02857 em sur ses boutons (0,46 px
             a 16 px), la planche 0 (libelle 3:60557). Pose ici et non dans le
             theme, qui habille aussi les boutons de l'admin. */
          letterSpacing:"0px",
          height:["45px !important", "50px !important"],
          minHeight:["45px !important", "50px !important"],
          /* ⚠️ La planche donne 15 px de marge interieure horizontale et
             17 px verticale (noeud 6:321, `px-[15px] py-[17px]`), dans un
             bouton de 150 px de large. Le code posait 25 a 40 px, soit jusqu'a
             80 px de marge interieure cumulee, et laissait la largeur au
             contenu : le bouton changeait de taille selon le libelle.
             `minWidth` plutot que `width` : a 150 px le plancher mord pour tous
             les libelles des planches, et un libelle plus long (traduction)
             s'etend au lieu d'etre coupe. */
          /* ⚠️ CORRIGE LE 22/09 : j'avais pose 150 px et 17 px de marge
             verticale. Relecture complete du fichier : le bouton dominant fait
             130 x 50 — 21 occurrences en Desktop, 19 en Tablet, 20 en Mobile —
             et descend a 45 px de haut en Mobile. Le 150 n'existe que sur la
             planche video et quelques autres, le 190 x 70 sur la planche Titre.
             Node 6:321 : libelle a x=15, chevron finissant a 115 sur 130, donc
             15 px de chaque cote ; libelle a y=19,5 sur 11 de haut, donc centre
             dans les 50. La hauteur est FIXEE, les 17 px verticaux etaient une
             lecture du padding d'auto-layout, pas de la boite rendue. */
          /* ⚠️ REPARTITION FIGMA : le bouton est `justify-between`, le libelle
             occupe toute la place restante et centre son texte, le chevron
             tombe contre la marge interieure droite. Le code laissait MUI
             centrer l'ensemble et collait le glyphe au libelle par une marge
             de 12 px : sur un libelle court, le chevron flottait au milieu. */
          justifyContent:"space-between",
          /* ⛔ 01/10/2026 — 10 PX DE RESPIRATION AUTOUR DU LIBELLE, ET C'EST LE
             POINT 6 D'HICHAM (« Retour au dashboard et la fleche trop colle »).
             Le libelle prend toute la place restante (`flex:1 0 0`) et centre
             son texte. Sur un libelle COURT c'est sans effet : « Valider » fait
             55 px dans une boite de 92,8, donc 19 px de jeu de chaque cote.
             Sur un libelle LONG la boite epouse le texte, le jeu tombe a ZERO,
             et les glyphes touchent le chevron. C'est exactement ce que montre
             la capture de la fin de module : « Retour au dashboard » fait 19
             caracteres la ou la planche en dessine 9.
             ⚠️ LA MARGE EST INTERIEURE, PAS UN `gap`. Un `gap` sur le bouton
             retirerait 10 px a la boite du libelle : elle passerait de 92,8 a
             82,8 et le texte centre se decalerait de 5 px — contre le node
             1171276225, qui donne 92,8. Avec `border-box` et un `padding`, la
             boite rendue reste a 92,8 (le texte est centre, donc symetrique :
             rien ne bouge), et seul le cas long fait grandir le bouton.
             Nodes : 3:60556 (le bouton, 130 x 50 a x=1001) et 3:60557 (la boite
             du libelle, 92,8 x 11 a x=1016), planche 02.
             Le banc ne pouvait pas l'attraper : son libelle est « Valider ». */
          "& .hs-cta-libelle": {
            flex:"1 0 0", minWidth:0, textAlign:"center",
            boxSizing:"border-box", paddingLeft:"10px", paddingRight:"10px",
          },
          minWidth:"130px",
          paddingLeft:"15px",
          paddingRight:"15px",
          paddingTop:"0px",
          paddingBottom:"0px",
          whiteSpace:"noWrap",
        }}
        size="large"
        type="submit"
        variant="contained" disableElevation
        disableRipple
      >
        <span className="hs-cta-libelle">
          <EditableField
            field={field}
            type={type}
            buttonName={buttonName?buttonName:"button"}
            isEditor={true}
          />
        </span>
        {fleche}
      </Button>
    )
  } else {
    return (
      <Button
        sx={{
          ...sx,
          /* ⚠️ RELU DANS FIGMA (21/09/2026, lot 3d), noeuds 3:20302 et
             3:40482 : 16 px / interligne 26, IDENTIQUE aux trois paliers. Le
             code descendait a 15 px en Mobile. */
          fontSize:"16px !important",
          lineHeight:"26px !important",
          /* Aucun interlettrage : MUI pose 0,02857 em sur ses boutons (0,46 px
             a 16 px), la planche 0 (libelle 3:60557). Pose ici et non dans le
             theme, qui habille aussi les boutons de l'admin. */
          letterSpacing:"0px",
          height:["45px !important", "50px !important"],
          minHeight:["45px !important", "50px !important"],
          /* ⚠️ La planche donne 15 px de marge interieure horizontale et
             17 px verticale (noeud 6:321, `px-[15px] py-[17px]`), dans un
             bouton de 150 px de large. Le code posait 25 a 40 px, soit jusqu'a
             80 px de marge interieure cumulee, et laissait la largeur au
             contenu : le bouton changeait de taille selon le libelle.
             `minWidth` plutot que `width` : a 150 px le plancher mord pour tous
             les libelles des planches, et un libelle plus long (traduction)
             s'etend au lieu d'etre coupe. */
          /* ⚠️ CORRIGE LE 22/09 : j'avais pose 150 px et 17 px de marge
             verticale. Relecture complete du fichier : le bouton dominant fait
             130 x 50 — 21 occurrences en Desktop, 19 en Tablet, 20 en Mobile —
             et descend a 45 px de haut en Mobile. Le 150 n'existe que sur la
             planche video et quelques autres, le 190 x 70 sur la planche Titre.
             Node 6:321 : libelle a x=15, chevron finissant a 115 sur 130, donc
             15 px de chaque cote ; libelle a y=19,5 sur 11 de haut, donc centre
             dans les 50. La hauteur est FIXEE, les 17 px verticaux etaient une
             lecture du padding d'auto-layout, pas de la boite rendue. */
          /* ⚠️ REPARTITION FIGMA : le bouton est `justify-between`, le libelle
             occupe toute la place restante et centre son texte, le chevron
             tombe contre la marge interieure droite. Le code laissait MUI
             centrer l'ensemble et collait le glyphe au libelle par une marge
             de 12 px : sur un libelle court, le chevron flottait au milieu. */
          justifyContent:"space-between",
          /* ⛔ 01/10/2026 — 10 PX DE RESPIRATION AUTOUR DU LIBELLE, ET C'EST LE
             POINT 6 D'HICHAM (« Retour au dashboard et la fleche trop colle »).
             Le libelle prend toute la place restante (`flex:1 0 0`) et centre
             son texte. Sur un libelle COURT c'est sans effet : « Valider » fait
             55 px dans une boite de 92,8, donc 19 px de jeu de chaque cote.
             Sur un libelle LONG la boite epouse le texte, le jeu tombe a ZERO,
             et les glyphes touchent le chevron. C'est exactement ce que montre
             la capture de la fin de module : « Retour au dashboard » fait 19
             caracteres la ou la planche en dessine 9.
             ⚠️ LA MARGE EST INTERIEURE, PAS UN `gap`. Un `gap` sur le bouton
             retirerait 10 px a la boite du libelle : elle passerait de 92,8 a
             82,8 et le texte centre se decalerait de 5 px — contre le node
             1171276225, qui donne 92,8. Avec `border-box` et un `padding`, la
             boite rendue reste a 92,8 (le texte est centre, donc symetrique :
             rien ne bouge), et seul le cas long fait grandir le bouton.
             Nodes : 3:60556 (le bouton, 130 x 50 a x=1001) et 3:60557 (la boite
             du libelle, 92,8 x 11 a x=1016), planche 02.
             Le banc ne pouvait pas l'attraper : son libelle est « Valider ». */
          "& .hs-cta-libelle": {
            flex:"1 0 0", minWidth:0, textAlign:"center",
            boxSizing:"border-box", paddingLeft:"10px", paddingRight:"10px",
          },
          minWidth:"130px",
          paddingLeft:"15px",
          paddingRight:"15px",
          paddingTop:"0px",
          paddingBottom:"0px",
          whiteSpace:"noWrap",
          /* Redesign Valentin 2026 (OBSERVED Figma, synthese Etape 1 §1 :
             « desactive = opacity 0.30 »). Etat desactive du CTA de PARCOURS :
             le bouton reste NOIR et s'estompe a 0.30, au lieu du gris MUI par
             defaut. Porte ici, sur le CTA de formation, et NON en global (pas de
             preuve Figma pour l'admin). Apparence seule : `disabled` desactive
             toujours le clic a l'identique. */
          "&.Mui-disabled": {
            backgroundColor:"#000000",
            color:"#ffffff",
            opacity:0.30,
          },
          ...(grand ? GRAND : {}),
        }}
        {...reste}
        disabled={disabled}
        onClick={onClick}
        size="large"
        type="submit"
        variant="contained" disableElevation
      >
        <span className="hs-cta-libelle">{field}</span>
        {fleche}
      </Button>
    )
  }
}

export default EditableButton
